import { useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useCartStore } from "@/stores/cartStore";
import { supabase } from "@/integrations/supabase/client";
import { Json } from "@/integrations/supabase/types";

export const useCartSync = () => {
  const { user, loading: authLoading } = useAuth();
  const { items } = useCartStore();
  const lastSyncRef = useRef<string>("");

  // Save cart to database for abandoned cart tracking
  const saveToDatabase = useCallback(async () => {
    if (!user || items.length === 0) return;

    const itemsJson = JSON.stringify(items);
    if (itemsJson === lastSyncRef.current) return;
    lastSyncRef.current = itemsJson;

    try {
      // Upsert abandoned cart
      const { data: existing } = await supabase
        .from("abandoned_carts")
        .select("id")
        .eq("user_id", user.id)
        .single();

      // Convert items to Json compatible format
      const jsonItems = JSON.parse(JSON.stringify(items)) as Json;

      if (existing) {
        await supabase
          .from("abandoned_carts")
          .update({ items: jsonItems, updated_at: new Date().toISOString() })
          .eq("user_id", user.id);
      } else {
        await supabase.from("abandoned_carts").insert([{
          user_id: user.id,
          items: jsonItems,
        }]);
      }
    } catch (error) {
      console.error("Error syncing cart to database:", error);
    }
  }, [user, items]);

  // Clear abandoned cart after successful purchase
  const clearAbandonedCart = useCallback(async () => {
    if (!user) return;

    try {
      await supabase
        .from("abandoned_carts")
        .delete()
        .eq("user_id", user.id);
    } catch (error) {
      console.error("Error clearing abandoned cart:", error);
    }
  }, [user]);

  // Sync cart to database when items change
  useEffect(() => {
    if (authLoading || !user) return;

    const timeoutId = setTimeout(() => {
      saveToDatabase();
    }, 2000); // Debounce 2 seconds

    return () => clearTimeout(timeoutId);
  }, [items, user, authLoading, saveToDatabase]);

  return {
    clearAbandonedCart,
  };
};
